import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { messageCallbackType, StompHeaders } from '@stomp/stompjs'

import { StompContext } from '@containers/StompSessionProvider/StompSessionProvider'

import { showSnackbarNotification } from '@helpers'

// TODO На текущий момент ws локально не работают
const isDev = false
// const isDev = process.env.NODE_ENV === 'development'

export const useSubscription = (
  destinations: string | string[],
  onMessage: messageCallbackType,
  headers: StompHeaders = {},
  onExpired?: (dest: string) => void
) => {
  const { t } = useTranslation()

  const [isSubscribed, setIsSubscribed] = useState(true)
  const stompContext = useContext(StompContext)

  if (stompContext === undefined) {
    throw new Error('There must be a StompSessionProvider as Ancestor of all Stomp Hooks and HOCs')
  }

  const { subscribe, subscribeToStale } = stompContext

  //работа с id подписки, т.к одновременно может быть несколько dest с одним именем
  const activeSubsRef = useRef<{ id: string; dest: string }[]>([])

  const handleDefaultExpire = useCallback(() => {
    showSnackbarNotification({
      text: t('websocketExpire.onExpireInfo'),
      options: {
        variant: 'warning',
        persist: true,
        preventDuplicate: true,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        alertSx: {
          alignItems: 'center',
        },
        additionalAction: {
          label: t('websocketExpire.refresh'),
          onClick: () => {
            window.location.reload()
          },
        },
        showCancel: true,
      },
    })
  }, [t])

  const callbackRef = useRef<messageCallbackType>(onMessage)
  const onExpiredCallbackRef = useRef(onExpired ?? handleDefaultExpire)

  callbackRef.current = onMessage
  onExpiredCallbackRef.current = onExpired ?? handleDefaultExpire

  const _destinations = Array.isArray(destinations) ? destinations : [destinations]

  // Создание подписок
  useEffect(() => {
    const cleanUpFunctions: (() => void)[] = []
    const currentSubs: { id: string; dest: string }[] = []

    if (isSubscribed) {
      _destinations.forEach(dest => {
        const { id, unsubscribe } = subscribe(
          dest,
          message => callbackRef.current(message),
          headers
        )

        currentSubs.push({ id, dest })
        cleanUpFunctions.push(unsubscribe)
      })

      activeSubsRef.current = currentSubs
    }

    return () => {
      cleanUpFunctions.forEach(unsub => unsub())
      activeSubsRef.current = []
    }
  }, [Object.values(_destinations).toString(), Object.values(headers).toString(), isSubscribed])

  useEffect(() => {
    return subscribeToStale(staleIds => {
      activeSubsRef.current.forEach(({ id, dest }) => {
        if (staleIds.has(id)) {
          console.log(`[WS] socket expired: ${dest}, at ${new Date().toLocaleTimeString()}`)

          onExpiredCallbackRef.current(dest)
        }
      })
    })
  }, [])

  return [setIsSubscribed]
}
